import ShaderEffect from "./shadereffect.js";

// the WGSL twin of the GLSL body below — same logic, same uniform
// names, picked by the ShaderEffect base per renderer.shaderLanguage
const wgslFragment = `
struct HologramUniforms {
	uHoloColor : vec3f,
	uHoloIntensity : f32,
	uTime : f32,
};
@group(3) @binding(0) var<uniform> fx : HologramUniforms;

fn apply(color : vec4f, uv : vec2f) -> vec4f {
	// scan line
	let scan = sin(uv.y * 200.0 + fx.uTime * 5.0) * 0.5 + 0.5;
	// flicker
	let flicker = 0.95 + 0.05 * sin(fx.uTime * 30.0);
	// color shift
	var holo = mix(color.rgb, fx.uHoloColor * color.a, fx.uHoloIntensity);
	holo *= (1.0 - scan * 0.15) * flicker;
	return vec4f(holo, color.a * flicker);
}
`;

/**
 * A shader effect that simulates a holographic projection with
 * horizontal scan lines, color shift, and flickering.
 * Commonly used for sci-fi UI, ghost/spirit characters, or tech displays.
 * The `time` uniform should be updated each frame for animation.
 * @category Effects
 * @see {@link Renderable#addPostEffect} for usage
 * @example
 * const holo = new HologramEffect(renderer);
 * mySprite.addPostEffect(holo);
 * // update each frame
 * holo.setTime(performance.now() / 1000);
 */
export default class HologramEffect extends ShaderEffect {
	/**
	 * @param {WebGLRenderer|WebGPURenderer|CanvasRenderer} renderer - the current renderer instance
	 * @param {object} [options] - effect options
	 * @param {number[]} [options.color=[0.1, 0.7, 1.0]] - hologram tint color [r, g, b]
	 * @param {number} [options.intensity=0.5] - effect intensity (0.0–1.0)
	 */
	constructor(renderer, options = {}) {
		super(renderer, {
			glsl: `
			uniform vec3 uHoloColor;
			uniform float uHoloIntensity;
			uniform float uTime;
			vec4 apply(vec4 color, vec2 uv) {
				// scan line
				float scan = sin(uv.y * 200.0 + uTime * 5.0) * 0.5 + 0.5;
				// flicker
				float flicker = 0.95 + 0.05 * sin(uTime * 30.0);
				// color shift
				vec3 holo = mix(color.rgb, uHoloColor * color.a, uHoloIntensity);
				holo *= (1.0 - scan * 0.15) * flicker;
				return vec4(holo, color.a * flicker);
			}
			`,
			wgsl: wgslFragment,
		});

		const color = options.color ?? [0.1, 0.7, 1.0];
		this.setUniform("uHoloColor", new Float32Array(color));
		this.setUniform("uHoloIntensity", options.intensity ?? 0.5);
		this.setUniform("uTime", 0.0);
	}

	/**
	 * set the hologram tint color
	 * @param {number[]} color - color as [r, g, b] (0.0–1.0)
	 */
	setColor(color) {
		this.setUniform("uHoloColor", new Float32Array(color));
	}
}
